import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";

import cls from "./CreateMemberPayment.module.scss";

/* Placeholders (same set used in CreateMemberAvatar) */
import mrImg from "@/assets/UI/memberCreate/q2Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q2MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q2Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q2MrsM.webp";

/* Framer: same “Welcome” gentle floating */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const formatMonthsLabel = (value) =>
  value === 1 ? "1 month" : `${value} months`;

export default function CreateMemberPayment() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  /* hero images */
  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const [walletBalance, setWalletBalance] = useState(0);
  const [monthlyCost, setMonthlyCost] = useState(150);
  const [availableMonths, setAvailableMonths] = useState(0);
  const [selectedMonths, setSelectedMonths] = useState(0);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const title = i18n.t("onboarding.payment.title", {
    defaultValue: "Membership payment",
  });

  const subtitle = i18n.t("onboarding.payment.subtitle", {
    defaultValue:
      "Use your Noty Coin balance to activate your membership instantly.",
  });

  const intro = i18n.t("onboarding.payment.intro", {
    defaultValue:
      "Your Noty Coin wallet is used for membership payments and renewals.",
  });

  const termsNotice = i18n.t("onboarding.payment.termsNotice", {
    defaultValue:
      "By submitting payment, you confirm you understand and accept the Terms and Conditions.",
  });

  const termsButtonLabel = i18n.t("onboarding.payment.termsButtonLabel", {
    defaultValue: "Open Terms and Conditions",
  });

  const walletTitle = i18n.t("onboarding.payment.walletTitle", {
    defaultValue: "Noty Coin wallet",
  });

  const walletHelp = i18n.t("onboarding.payment.walletHelp", {
    defaultValue:
      "Membership costs 150 Noty Coin per month. Select how many months you want based on your balance.",
  });

  const balanceLabel = i18n.t("onboarding.payment.balanceLabel", {
    defaultValue: "Balance",
  });

  const costLabel = i18n.t("onboarding.payment.costLabel", {
    defaultValue: "Monthly cost",
  });

  const availableLabel = i18n.t("onboarding.payment.availableLabel", {
    defaultValue: "Available months",
  });

  const monthsLabel = i18n.t("onboarding.payment.monthsLabel", {
    defaultValue: "How many months do you want?",
  });

  const totalLabel = i18n.t("onboarding.payment.totalLabel", {
    defaultValue: "Total cost",
  });

  const refillTitle = i18n.t("onboarding.payment.refillTitle", {
    defaultValue: "Refill required",
  });

  const refillBody = i18n.t("onboarding.payment.refillBody", {
    defaultValue:
      "You need at least 150 Noty Coin to activate your membership. Please refill your wallet to continue.",
  });

  const refillCta = i18n.t("onboarding.payment.refillCta", {
    defaultValue: "Refill wallet",
  });

  const ctaSave = i18n.t("onboarding.payment.submitCta", {
    defaultValue: "Confirm membership payment",
  });

  const loadErrorMsg = i18n.t("onboarding.payment.loadError", {
    defaultValue: "Couldn’t load wallet info. Please try again.",
  });

  const selectMonthsMsg = i18n.t("onboarding.payment.needMonths", {
    defaultValue: "Please select how many months you want.",
  });

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setLoadingWallet(true);
        const { data } = await api.get(
          "/v1/member-create/payment/wallet-status",
        );
        if (!isMounted) return;

        const balance = Number(data?.balanceNoty ?? 0);
        const cost = Number(data?.monthlyCostNoty ?? 150);
        const months = Number(data?.availableMonths ?? 0);

        setWalletBalance(balance);
        setMonthlyCost(cost);
        setAvailableMonths(months);
        setSelectedMonths(months > 0 ? 1 : 0);
      } catch (err) {
        console.error("load wallet balance failed:", err);
        toast.error(loadErrorMsg);
      } finally {
        if (isMounted) setLoadingWallet(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [loadErrorMsg]);

  async function onSubmit(e) {
    e.preventDefault();

    if (!selectedMonths) {
      toast.info(selectMonthsMsg);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        months: selectedMonths,
      };

      const { data } = await api.post(
        "/v1/member-create/payment/wallet-submit",
        payload,
      );

      await refreshMe();
      toast.success(
        i18n.t("onboarding.payment.afterSubmit", {
          defaultValue:
            "Thanks! Your membership is renewed and pending admin review.",
        }),
      );

      navigate(data?.next || "/member/pending", { replace: true });
    } catch (err) {
      console.error("submit member payment failed:", err);
      toast.error(
        i18n.t("onboarding.payment.submitError", {
          defaultValue:
            "Couldn’t submit payment. Please check your details and try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const totalCost = selectedMonths * monthlyCost;
  const submitDisabled =
    submitting || loadingWallet || availableMonths < 1 || !selectedMonths;

  const termsHref =
    i18n.t("onboarding.payment.termsHref", { defaultValue: "/terms" }) ||
    "/terms";

  return (
    <main className={cls.page}>
      {/* Fixed background stage (Welcome-style) */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>{subtitle}</div>

                <div className={cls.metaNotes}>
                  <p className={cls.metaLine}>{intro}</p>
                  <p className={cls.metaLine}>{termsNotice}</p>

                  <button
                    type="button"
                    className={cls.termsBtn}
                    onClick={() => window.open(termsHref, "_blank", "noopener")}
                  >
                    {termsButtonLabel}
                  </button>
                </div>
              </div>
            </div>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              <section className={cls.infoBlock}>
                <div className={cls.infoTitle}>{walletTitle}</div>
                <div className={cls.infoNote}>{walletHelp}</div>

                {loadingWallet ? (
                  <div className={cls.infoNote}>
                    {i18n.t("common.loading", { defaultValue: "Loading…" })}
                  </div>
                ) : (
                  <div className={cls.walletSummary}>
                    <div className={cls.walletRow}>
                      <span className={cls.walletLabel}>{balanceLabel}</span>
                      <span className={cls.walletValue}>
                        {walletBalance} Noty
                      </span>
                    </div>
                    <div className={cls.walletRow}>
                      <span className={cls.walletLabel}>{costLabel}</span>
                      <span className={cls.walletValue}>
                        {monthlyCost} Noty
                      </span>
                    </div>
                    <div className={cls.walletRow}>
                      <span className={cls.walletLabel}>{availableLabel}</span>
                      <span className={cls.walletValue}>{availableMonths}</span>
                    </div>
                  </div>
                )}
              </section>

              {availableMonths < 1 && !loadingWallet && (
                <section className={cls.warningCard}>
                  <div className={cls.infoTitle}>{refillTitle}</div>
                  <div className={cls.infoNote}>{refillBody}</div>
                  <Link className={cls.linkButton} to="/member/wallet">
                    {refillCta}
                  </Link>
                </section>
              )}

              {availableMonths > 0 && (
                <div className={cls.fieldGroup}>
                  <label className={cls.fieldLabel} htmlFor="months">
                    {monthsLabel}
                  </label>
                  <div className={cls.selectWrap}>
                    <select
                      id="months"
                      className={cls.select}
                      value={selectedMonths}
                      onChange={(e) =>
                        setSelectedMonths(Number(e.target.value))
                      }
                    >
                      {[...Array(availableMonths)].map((_, idx) => {
                        const value = idx + 1;
                        return (
                          <option key={value} value={value}>
                            {formatMonthsLabel(value)}
                          </option>
                        );
                      })}
                    </select>
                    <span className={cls.selectArrow} aria-hidden="true" />
                  </div>
                  <div className={cls.fieldHint}>
                    {totalLabel}: {totalCost} Noty
                  </div>
                </div>
              )}

              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitDisabled}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting
                    ? i18n.t("common.saving", { defaultValue: "Saving…" })
                    : ctaSave}
                </button>
              </div>

              <div className={cls.helpNote}>
                <span className={cls.helpDot} aria-hidden="true" />
                {i18n.t("onboarding.payment.note", {
                  defaultValue:
                    "Tip: Need more Noty Coin? You can refill your wallet anytime.",
                })}
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
